import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { getColorsArrayByIds } from "utils/UtilityFuncs";

export default function QuickViewProducts({
  user,
  selected,
  selectedProduct,
  setSelectedProduct,
  productsList,
  setProductsList,
  productTypesList,
  refreshFunc,
  allProducts,
  setAllProducts,
  allWOData,
  setAllWOData,
  pendingProductTypeUpdates,
  setPendingProductTypeUpdates,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const [approvalColorNames, setApprovalColorNames] = useState(null);

  /*   All Functions
   ********************************************* */
  const handleProductTypeSelection = async (e) => {
    const nextTypeId = parseInt(e.target.value, 10);
    let selectedType = productTypesList.find(
      (item) => item?.productTypeId === nextTypeId,
    );

    if (!selectedProduct || !Number.isInteger(nextTypeId)) {
      dispatch(
        setNotification({
          message: "Please select a product to update.",
          type: "error",
        }),
      );
      return;
    }

    const updateData = {
      id: selectedProduct.id,
      productType: selectedType?.productTypes,
      productTypeId: selectedType?.productTypeId,
    };

    const sourceProduct = allProducts?.find(
      (item) => item.id === selectedProduct.id,
    );
    const originalTypeId = sourceProduct?.productTypeId;
    const currentDbTypeId =
      pendingProductTypeUpdates?.[selectedProduct.id]?.productTypeId ||
      originalTypeId;

    if (currentDbTypeId === nextTypeId) {
      setSelectedProduct((prev) =>
        prev?.id === selectedProduct.id
          ? {
              ...prev,
              productType: selectedType?.productTypes,
              productTypeId: selectedType?.productTypeId,
            }
          : prev,
      );
      return;
    }

    try {
      setLoading(true);

      const { data } = await API.patch(
        "/wo-products",
        { data: updateData },
        {
          headers: authHeader(),
        },
      );

      if (data?.message?.type === "success") {
        if (originalTypeId === nextTypeId) {
          setPendingProductTypeUpdates((prev = {}) => {
            const updated = { ...prev };
            delete updated[selectedProduct.id];
            return updated;
          });
        } else {
          setPendingProductTypeUpdates((prev = {}) => ({
            ...prev,
            [selectedProduct.id]: updateData,
          }));
        }

        setSelectedProduct((prev) =>
          prev?.id === selectedProduct.id
            ? {
                ...prev,
                productType: selectedType?.productTypes,
                productTypeId: selectedType?.productTypeId,
              }
            : prev,
        );
        return;
      }

      dispatch(
        setNotification({
          message: data?.message?.message || "Failed to update product type.",
          type: "error",
        }),
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const UpdateCurrentProductReady = async ({ ready }) => {
    if (!selectedProduct?.id) return;

    const currentProductId = selectedProduct.id;

    try {
      setLoading(true);

      const { data } = await API.patch(
        `/wo-products/ready-status/${currentProductId}`,
        {
          WOId: selected?.id,
          isReady: ready,
        },
        { headers: authHeader() },
      );

      if (data?.success) {
        // Keep all product state slices in sync while navigating.
        setAllProducts((prev = []) =>
          prev.map((product) =>
            product.id === currentProductId
              ? { ...product, isReady: ready }
              : product,
          ),
        );

        setProductsList((prev = []) =>
          prev.map((product) =>
            product.id === currentProductId
              ? { ...product, isReady: ready }
              : product,
          ),
        );

        setSelectedProduct((prev) =>
          prev?.id === currentProductId ? { ...prev, isReady: ready } : prev,
        );

        dispatch(
          setNotification({
            message: data.message,
            type: "success",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const UpdatePurchaseStatus = async ({ purchased, received }) => {
    if (!selectedProduct?.id) return;

    const currentProductId = selectedProduct.id;

    // Merge with current values so both fields are always sent together
    const nextPurchased =
      purchased !== undefined ? purchased : !!selectedProduct?.purchased;
    const nextReceived =
      received !== undefined ? received : !!selectedProduct?.received;

    try {
      setLoading(true);

      const { data } = await API.patch(
        `/wo-products/purchase-status`,
        {
          id: currentProductId,
          purchased: nextPurchased,
          received: nextReceived,
        },
        { headers: authHeader() },
      );

      if (data?.success) {
        const updates = { purchased: nextPurchased, received: nextReceived };

        setAllProducts((prev = []) =>
          prev.map((product) =>
            product.id === currentProductId
              ? { ...product, ...updates }
              : product,
          ),
        );

        setProductsList((prev = []) =>
          prev.map((product) =>
            product.id === currentProductId
              ? { ...product, ...updates }
              : product,
          ),
        );

        setSelectedProduct((prev) =>
          prev?.id === currentProductId ? { ...prev, ...updates } : prev,
        );

        dispatch(
          setNotification({
            message: data.message,
            type: "success",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // Get Index of selected Product
  const getSelectedProductIndex = () => {
    if (productsList?.length === 0) {
      return;
    }
    let currentIndex = productsList.findIndex(
      (item) => item.id === selectedProduct.id,
    );
    return currentIndex;
  };

  //  Next Product
  const nextProduct = () => {
    if (productsList?.length === 0) {
      return;
    }
    let currentIndex = getSelectedProductIndex();

    // If the current index is the last index
    if (currentIndex === productsList.length - 1) {
      setSelectedProduct(productsList[0]);
      return;
    }

    setSelectedProduct(productsList[currentIndex + 1]);
  };

  //  Previous Product
  const prevProduct = () => {
    if (productsList?.length === 0) {
      return;
    }
    let currentIndex = getSelectedProductIndex();

    // If the current index is the first index
    if (currentIndex === 0) {
      setSelectedProduct(productsList[productsList.length - 1]);
      return;
    }

    setSelectedProduct(productsList[currentIndex - 1]);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let approvalColorIds = selectedProduct?.approvalColors;
    if (approvalColorIds) {
      setLoading(true);
      getColorsArrayByIds(approvalColorIds)
        .then((colorArr) => {
          setApprovalColorNames(colorArr.map((color) => color.name).join(", "));
          setLoading(false);
        })
        .catch((err) => {
          setApprovalColorNames(null);
          setLoading(false);
        });
    } else {
      setApprovalColorNames(null);
      setLoading(false);
    }
  }, [selectedProduct]);

  return (
    <div>
      {loading && <Spinner />}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4
          className="flex-grow-1 text-nowrap me-3"
          style={{
            width: "50%",
          }}
        >
          Product
          {` {${
            productsList?.length === 0
              ? " 0 "
              : ` ${getSelectedProductIndex() + 1} `
          } of ${productsList?.length} }`}
        </h4>

        <div
          className="flex-grow-1 d-flex align-items-center  justify-content-end "
          style={{
            width: "35%",
          }}
        >
          {selectedProduct && (
            <>
              {/* Pending & Received */}
              <div className="d-flex align-items-center gap-1">
                {/* Approval Status */}
                <span
                  className={`btn btn-sm rounded-3 d-flex align-items-center justify-content-center pe-none ${
                    selectedProduct?.approveStatus === "approved"
                      ? "btn-success"
                      : selectedProduct?.approveStatus === "rejected"
                        ? "btn-danger"
                        : "btn-secondary"
                  }`}
                  style={{ width: "25px", height: "25px" }}
                  title={selectedProduct?.approveStatus || "pending"}
                >
                  A
                </span>
                <div className="d-flex align-items-center ">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      UpdatePurchaseStatus({
                        purchased: !selectedProduct?.purchased,
                      });
                    }}
                    className={`btn btn-sm rounded-3 d-flex align-items-center justify-content-center ${
                      selectedProduct?.purchased
                        ? "btn-success"
                        : "btn-secondary"
                    }`}
                    style={{ width: "25px", height: "25px" }}
                    aria-label="Toggle purchased"
                  >
                    P
                  </button>
                </div>
                <div className="d-flex align-items-center ">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      UpdatePurchaseStatus({
                        received: !selectedProduct?.received,
                      });
                    }}
                    className={`me-1 btn btn-sm rounded-3 d-flex align-items-center justify-content-center ${
                      selectedProduct?.received
                        ? "btn-success"
                        : "btn-secondary"
                    }`}
                    style={{ width: "25px", height: "25px" }}
                    aria-label="Toggle received"
                  >
                    R
                  </button>
                </div>
                {/* Ready */}
                <div className="d-flex align-items-center gap-1 me-3 ">
                  <label className="col-5 me-1">Ready?</label>
                  <div className="col-7 flex-center justify-content-start">
                    <input
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        UpdateCurrentProductReady({ ready: e.target.checked });
                      }}
                      // disabled={!selectedProductType}
                      checked={selectedProduct?.isReady}
                      type="checkbox"
                      id="switch2"
                      data-switch="success"
                    />
                    <label
                      htmlFor="switch2"
                      data-on-label="Yes"
                      data-off-label="No"
                    ></label>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div
          className="flex-grow-1 d-flex justify-content-end gap-1 p-1"
          style={{
            width: "15%",
          }}
        >
          {/*   WO Product Buttons
           ********************************************* */}
          {productsList?.length > 1 && (
            <>
              <button
                onClick={() => {
                  prevProduct();
                }}
                disabled={productsList?.length === 0}
                className={"btn btn-success btn-sm p-0 px-1 "}
              >
                <i className="mdi mdi-arrow-left-bold fs-4 py-0 my-0"></i>
              </button>
              <button
                onClick={() => {
                  nextProduct();
                }}
                disabled={productsList?.length === 0}
                className={"btn btn-success btn-sm p-0 px-1"}
              >
                <i className="mdi mdi-arrow-right-bold fs-4 py-0 my-0"></i>
              </button>
            </>
          )}
        </div>
      </div>
      <div className=" p-1">
        <div className="row mb-0">
          <label className="col-5 text-end">Product Type</label>
          {productTypesList?.length === 0 ? (
            <input
              className="form-control col-7 w-50 form-control w-fit"
              readOnly
              value={selectedProduct?.productTypes?.productTypes || ""}
            />
          ) : (
            <select
              disabled={!selectedProduct}
              value={
                pendingProductTypeUpdates?.[selectedProduct?.id]
                  ?.productTypeId ||
                (selectedProduct?.productTypeId
                  ? selectedProduct.productTypeId
                  : "")
              }
              className="col-7 w-50 form-select w-fit"
              onChange={handleProductTypeSelection}
            >
              {productTypesList &&
                productTypesList?.map((item) => (
                  <option key={item?.id} value={item?.productTypeId}>
                    {item?.productTypes}
                  </option>
                ))}
            </select>
          )}
        </div>
        <div className="row mb-0">
          <label className="col-5 text-end">Product Style</label>
          <div className="col-7 d-flex align-items-center">
            {selectedProduct?.productStyle}
          </div>
        </div>
        <div className="row mb-0">
          <label className="col-5 text-end">Product Description</label>
          <div className="col-7 ">{selectedProduct?.productDescription}</div>
        </div>
        <div className="row mb-0">
          <label className="col-5 text-end">Product Fabric</label>
          <div className="col-7 ">{selectedProduct?.productFabric}</div>
        </div>
        <div className="row mb-0">
          <label className="col-5 text-end">Product Color</label>
          <div className="col-7 ">
            {approvalColorNames || selectedProduct?.productColor}
          </div>
        </div>
        {/* <div className="row mb-0">
          <label className="col-5 text-end">Team Name</label>
          <div className="col-7 ">{selectedProduct?.teamName}</div>
        </div> */}
        <div className="row mb-0">
          <label className="col-5 text-end">Team Name</label>
          <div className="col-7 overflow-hidden text-truncate">
            <div className="col-7 ">{selectedProduct?.teamName}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
